import { useRef, useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { socketService } from '@/services/SocketService'
import { describeStepChange, watchableState } from '@/utils/describeStepChange'
import { saveBeforeLeaving } from '@/utils/saveBeforeLeaving'
import { keepaliveFromSessionReady, useVoiceKeepalive, type VoiceKeepalive } from '@/hooks/useVoiceKeepalive'
import { voiceAmplitude } from '@/services/voiceAmplitude'
import {
  setVoiceStatus,
  setTranscript,
  clearStreamingTranscript,
  setHighlightedField,
  setSessionId,
  setSessionNotice,
  setDebugTextInputEnabled,
  resetVoice,
  type VoiceStatus,
} from '@/redux/slices/onboardingVoiceSlice'
import { useAudioCapture } from './useAudioCapture'
import type { StepHandle, SubmitResult, PlaceCandidate } from '@/components/Onboarding/stepHandle'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Lightweight, step-agnostic context the agent needs every sync. */
export interface OnboardingVoiceMeta {
  currentStep: string
  /** Steps already completed or skipped — drives the "don't re-ask" shortcut. */
  completedSteps: string[]
  /** Company name from the tenant settings API — injected into the confirmation trigger so
   *  SAM can greet with the real name without a get_current_state() round-trip. */
  company_name?: string
}

/** Navigation callbacks owned by the wizard page. */
export interface OnboardingVoiceNav {
  goNext: () => void
  goBack: () => void
  skipStep: () => void
  skipToDashboard: () => void
  launch: () => void
  goToStep: (stepId: string) => void
  /** Finish for real: complete onboarding, end the voice session, and open the dashboard/chat.
   *  Called when the completion agent's `open_dashboard` action fires (user confirmed on the summary). */
  openDashboard: () => void
}

/**
 * Callbacks for profile / voice steps (steps 9–10).
 * Used by pages that don't have StepHandle refs (e.g. UserOnboardingPage).
 * All fields are optional so the existing wizard callers don't need to change.
 */
export interface UserStepCallbacks {
  onSetFullName?: (v: string) => void
  onSetPhone?: (v: string) => void
  onSetMode?: (m: 'text' | 'voice') => void
  onSetVoice?: (id: string) => void
  onFinish?: () => void
  /** Return the current step's state snapshot for state_sync messages. */
  getState?: () => Record<string, unknown> | null
}

// ---------------------------------------------------------------------------
// TTS audio queue helpers — per-hook refs (not module globals) to avoid
// state leakage across remounts and React StrictMode double-invocations.
// ---------------------------------------------------------------------------

function createAudioPlayer(onDrained?: () => void) {
  let audioCtx: AudioContext | null = null
  let analyser: AnalyserNode | null = null
  let rafId = 0
  // Gapless scheduling: chunks are scheduled back-to-back on the AudioContext clock rather than
  // started serially on each previous chunk's `onended` (which left an event-loop-sized gap between
  // every chunk — audible as crackle/hiss, worse at higher speeds where chunks are shorter).
  const pending: Float32Array<ArrayBuffer>[] = []     // decoded chunks awaiting scheduling
  const scheduled = new Set<AudioBufferSourceNode>()  // live (scheduled, not yet ended) sources
  let nextStartTime = 0                               // ctx-clock time the next chunk should start
  let pumping = false                                 // guard against concurrent pump() (async resume)

  function getCtx(): AudioContext {
    if (!audioCtx || audioCtx.state === 'closed') {
      audioCtx = new AudioContext({ sampleRate: 24000, latencyHint: 'interactive' })
      // Analyser sits between the source nodes and the speakers, tapping the
      // output level so the avatar can pulse in time with SAM's voice.
      analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      analyser.connect(audioCtx.destination)
    }
    return audioCtx
  }

  // rAF loop: publish output RMS (0..1) to voiceAmplitude while audio is flowing.
  function startMeter(): void {
    if (rafId || !analyser) return
    const buf = new Uint8Array(analyser.fftSize)
    const tick = () => {
      if (!analyser) { rafId = 0; return }
      analyser.getByteTimeDomainData(buf)
      let sum = 0
      for (let i = 0; i < buf.length; i++) {
        const v = (buf[i] - 128) / 128
        sum += v * v
      }
      const rms = Math.sqrt(sum / buf.length)
      voiceAmplitude.set(rms * 2.2) // speech RMS ~0.05–0.45 → roughly fill 0..1
      if (scheduled.size > 0 || pending.length > 0) {
        rafId = requestAnimationFrame(tick)
      } else {
        rafId = 0
        voiceAmplitude.set(0)
        onDrained?.()
      }
    }
    rafId = requestAnimationFrame(tick)
  }

  function stopMeter(): void {
    if (rafId) { cancelAnimationFrame(rafId); rafId = 0 }
    voiceAmplitude.set(0)
  }

  // Schedule every pending chunk back-to-back on the AudioContext clock. Non-blocking: we do NOT
  // wait for a chunk to finish before scheduling the next, so there is no inter-chunk gap. The only
  // async step is resuming a suspended context; `pumping` guards against overlapping pumps and any
  // chunks that arrive during the await are drained on the next pass.
  async function pump(): Promise<void> {
    if (pumping) return
    pumping = true
    try {
      const ctx = getCtx()
      startMeter() // analyser exists once getCtx has run
      if (ctx.state === 'suspended') {
        try { await ctx.resume() } catch { /* ignore */ }
      }
      while (pending.length > 0) {
        const float32 = pending.shift()!
        const audioBuffer = ctx.createBuffer(1, float32.length, 24000)
        audioBuffer.copyToChannel(float32, 0)

        const source = ctx.createBufferSource()
        source.buffer = audioBuffer
        source.connect(analyser ?? ctx.destination)

        const now = ctx.currentTime
        // If we've fallen behind (first chunk, or a silence gap since the last utterance), restart
        // the schedule slightly ahead of 'now' to avoid an immediate underrun; otherwise chain
        // seamlessly off the previously scheduled end time.
        if (nextStartTime < now) nextStartTime = now + 0.02
        source.start(nextStartTime)
        nextStartTime += audioBuffer.duration

        scheduled.add(source)
        source.onended = () => { scheduled.delete(source) }
      }
    } finally {
      pumping = false
    }
    // Chunks may have arrived while we awaited resume(); drain them.
    if (pending.length > 0) void pump()
  }

  function enqueue(base64: string): void {
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    const int16 = new Int16Array(bytes.buffer)
    const float32 = new Float32Array(int16.length)
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 32768
    pending.push(float32)
    void pump()
  }

  function clear(): void {
    stopMeter()
    for (const source of scheduled) {
      try { source.stop() } catch { /* already ended */ }
    }
    scheduled.clear()
    pending.length = 0
    nextStartTime = 0
    analyser = null
    if (audioCtx && audioCtx.state !== 'closed') {
      audioCtx.close().catch(() => undefined)
      audioCtx = null
    }
  }

  function isDrained(): boolean {
    return pending.length === 0 && scheduled.size === 0
  }

  return { enqueue, clear, isDrained }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages the onboarding voice session between the browser and api-service via
 * Socket.IO → Redis pub/sub. The agent drives the wizard through `onboarding:action`
 * events (translated by {@link dispatchAction}) and reads the current step via
 * `onboarding:get_state` (answered by {@link sendStateSync}).
 *
 * @param getMeta          Returns the current step + completed steps (called per sync).
 * @param getActiveHandle  Returns the mounted step's imperative handle (re-evaluated per action).
 * @param nav              Wizard navigation callbacks.
 */
export function useOnboardingVoice(
  getMeta: () => OnboardingVoiceMeta,
  getActiveHandle: () => StepHandle | null,
  nav: OnboardingVoiceNav,
  userStepCallbacks?: UserStepCallbacks,
) {
  const dispatch = useDispatch()

  // Keep stable refs so socket handlers never close over stale values.
  const getMetaRef = useRef(getMeta)
  const getActiveHandleRef = useRef(getActiveHandle)
  const navRef = useRef(nav)
  const userStepCbRef = useRef(userStepCallbacks)
  useEffect(() => {
    getMetaRef.current = getMeta
    getActiveHandleRef.current = getActiveHandle
    navRef.current = nav
    userStepCbRef.current = userStepCallbacks
  })

  // Most recent submit() outcome, surfaced to the agent so it learns why "next" didn't advance.
  const lastSubmitResultRef = useRef<SubmitResult | null>(null)

  // Mic mute: suppress outgoing audio without ending the session.
  const isMutedRef = useRef(false)
  const [isMuted, setIsMuted] = useState(false)
  // Set from onboarding:session_ready; drives the onboarding.keepalive pings (sent while muted too).
  const [keepalive, setKeepalive] = useState<VoiceKeepalive | null>(null)
  useVoiceKeepalive('onboarding', keepalive)

  const toggleMute = useCallback(() => {
    const next = !isMutedRef.current
    isMutedRef.current = next
    setIsMuted(next)
  }, [])

  // Speaker mute: suppress agent TTS playback without ending the session.
  const isSpeakerMutedRef = useRef(false)
  const [isSpeakerMuted, setIsSpeakerMuted] = useState(false)

  const toggleSpeakerMute = useCallback(() => {
    const next = !isSpeakerMutedRef.current
    isSpeakerMutedRef.current = next
    setIsSpeakerMuted(next)
    // If muting mid-speech, clear the queued audio immediately.
    if (next) audioPlayerRef.current.clear()
  }, [])

  // Transcript visibility: show/hide the live conversation transcript. Session-scoped UI state
  // (defaults to visible); remembered for the duration of this hook instance.
  const [showTranscript, setShowTranscript] = useState(true)
  const toggleTranscript = useCallback(() => setShowTranscript((v) => !v), [])

  // Track whether this hook instance has an active session.
  const isActiveRef = useRef(false)

  // Creates an audio player (no mic-gating callback needed — mic runs continuously).
  const makePlayer = useCallback(() => {
    return createAudioPlayer()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Per-instance audio player — avoids module-global state issues.
  const audioPlayerRef = useRef(makePlayer())

  // Stable session-id ref — set when startVoice creates the session.
  const sessionIdRef = useRef<string>('')

  // Tracks whether the user has already heard the SAM intro greeting this page load.
  // Reset on mount (page refresh). Prevents the full intro replaying on stop→restart.
  const hasHeardIntroRef = useRef(false)

  // ── Manual-edit watcher ──────────────────────────────────────────────────
  // Detects when the user changes the form by hand (typing, dropdown, toggle) and informs the
  // realtime agent via onboarding.user_input. Diff-based so it works for every step uniformly
  // without coupling to each field's onChange.
  const lastSnapshotRef = useRef<string>('')   // last snapshot we acted on (emitted/absorbed)
  const lastSeenRef = useRef<string>('')        // snapshot at the previous tick (settle detection)
  const lastAgentActionAtRef = useRef<number>(0)
  const editWatcherRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // -------------------------------------------------------------------------
  // State sync — send the current UI snapshot back to the agent
  // -------------------------------------------------------------------------
  const sendStateSync = useCallback(() => {
    // Only meaningful during a live voice session; safe to call freely (e.g. from a step-change
    // effect) — no-ops when voice isn't active so we don't emit stray syncs during manual onboarding.
    if (!isActiveRef.current) return
    const meta = getMetaRef.current()
    const handle = getActiveHandleRef.current()
    // Prefer the StepHandle's getState; fall back to the userStepCallbacks.getState for pages
    // that don't use imperative handles (e.g. UserOnboardingPage).
    const stepState = handle?.getState() ?? userStepCbRef.current?.getState?.() ?? null
    socketService.emit('message', {
      type: 'onboarding.state_sync',
      session_id: sessionIdRef.current,
      currentStep: meta.currentStep,
      completedSteps: meta.completedSteps,
      stepState,
      lastSubmitResult: lastSubmitResultRef.current,
    })
  }, [])

  // -------------------------------------------------------------------------
  // Action dispatcher — maps agent tool calls to step actuators / navigation.
  // Re-reads the active handle per call so it always targets the mounted step.
  // -------------------------------------------------------------------------
  const dispatchAction = useCallback(async (msg: Record<string, unknown>) => {
    // Mark the moment of an agent-driven change so the manual-edit watcher doesn't report the
    // agent's own field updates back as "user input".
    lastAgentActionAtRef.current = Date.now()
    const h = getActiveHandleRef.current()
    const nv = navRef.current
    const cb = userStepCbRef.current
    const action = msg.action as string
    const target = msg.target as string
    const value = msg.value as string

    // Profile / voice step actions (steps 9–10). These are dispatched when the agent has no
    // StepHandle to call through. Check current step to distinguish profile 'phone' from
    // team_onboarding 'phone' (both use fill_field but are step-guarded on the backend).
    const currentStep = getMetaRef.current().currentStep
    const isUserStep = currentStep === 'profile' || currentStep === 'voice'

    switch (action) {
      case 'fill_field':
        if (isUserStep && target === 'fullName') {
          cb?.onSetFullName?.(value)
        } else if (isUserStep && target === 'phone') {
          cb?.onSetPhone?.(value)
        } else {
          h?.setField?.(target, value)
        }
        break

      case 'set_interaction_mode':
        cb?.onSetMode?.(value === 'voice_and_text' ? 'voice' : 'text')
        break

      case 'set_voice':
        cb?.onSetVoice?.(value)
        break

      case 'finish':
        cb?.onFinish?.()
        break

      case 'set_color':
        h?.setField?.('brand_color', value)
        break

      case 'toggle_option':
        h?.toggleOption?.(target, msg.selected as boolean)
        break

      case 'add_territory':
        h?.addTerritory?.((msg.value ?? target) as string)
        break

      case 'remove_territory':
        h?.removeTerritory?.((msg.value ?? target) as string)
        break

      case 'set_notification_cell':
        h?.setMatrixCell?.(msg.classification as string, msg.channel as string, msg.enabled as boolean)
        break

      case 'set_quiet_hours':
        h?.setQuietHours?.(msg.enabled as boolean, msg.from as string | undefined, msg.to as string | undefined)
        break

      case 'set_team_role':
        h?.setRole?.(target, msg.selected as boolean)
        break

      case 'add_team_member':
        // The visible draft fields were filled one-by-one via fill_field/set_team_role. The step reports the outcome in
        // its state (lastActionResult), tagged with this request id.
        await h?.addTeamMember?.(msg.request_id as string | undefined)
        break

      case 'remove_team_member':
        // value = the invitation id (or email / exact name); the step cancels it and reports the outcome in its
        // state (lastActionResult), tagged with this request id.
        await h?.removeTeamMember?.((msg.value ?? target) as string, msg.request_id as string | undefined)
        break

      case 'toggle_role_visibility':
        h?.toggleRoleVisibility?.(target, msg.visible as boolean)
        break

      case 'update_role_mapping':
        h?.updateRoleMappings?.(target, (msg.add as string[]) ?? [], (msg.remove as string[]) ?? [])
        break

      case 'add_role':
        h?.addRole?.(target, (msg.base_roles as string[]) ?? [])
        break

      case 'remove_role':
        h?.removeRole?.(target)
        break

      case 'rename_role':
        h?.renameRole?.(target, msg.new_name as string)
        break

      case 'navigate': {
        // Idempotency / duplicate-call protection: if we're already on the step the agent expects
        // to land on, ignore this call. Guards against duplicate socket messages / retries
        // advancing the user multiple steps.
        const navTarget = msg.target as string | undefined
        if (navTarget && getMetaRef.current().currentStep === navTarget) break
        if (msg.direction === 'next') {
          const r = (await h?.submit?.()) ?? { ok: true }
          lastSubmitResultRef.current = r
          if (r.ok) nv.goNext()
          else sendStateSync() // surface validation/transient outcome to the agent
        } else if (msg.direction === 'back') {
          await saveBeforeLeaving(h)
          nv.goBack()
        }
        break
      }

      case 'navigate_to_step':
        // A detour must not throw away what was just set on this step (it is only saved on Next otherwise).
        await saveBeforeLeaving(h)
        nv.goToStep(msg.step as string)
        break

      case 'skip_step':
        nv.skipStep()
        break

      case 'skip_to_dashboard':
        nv.skipToDashboard()
        break

      case 'open_dashboard':
        // Completion agent confirmed the user is done on the summary screen — finish + open chat.
        nv.openDashboard()
        break

      case 'launch':
        nv.launch()
        break

      case 'highlight':
        dispatch(setHighlightedField(target))
        setTimeout(() => dispatch(setHighlightedField(null)), 2000)
        break

      case 'show_place_candidates':
        h?.setCandidates?.((msg.candidates ?? []) as PlaceCandidate[])
        break
    }
  }, [dispatch, sendStateSync])

  // -------------------------------------------------------------------------
  // Audio capture — sends PCM16 chunks to api-service via Socket.IO
  // -------------------------------------------------------------------------
  const handleAudioChunk = useCallback((int16: Int16Array) => {
    if (isMutedRef.current) return
    const bytes = new Uint8Array(int16.buffer)
    let binary = ''
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    const b64 = btoa(binary)
    socketService.emit('message', {
      type: 'onboarding.audio_chunk',
      session_id: sessionIdRef.current,
      data: b64,
    })
  }, [])

  const audioCapture = useAudioCapture(handleAudioChunk)

  // -------------------------------------------------------------------------
  // Socket.IO event listeners
  // -------------------------------------------------------------------------
  useEffect(() => {
    const onStatus = (data: unknown) => {
      // Drop status updates that arrive after the user stopped the session (e.g. an in-flight
      // "listening" event from the backend arriving after stopVoice cleared isActiveRef). Without
      // this guard, clicking Stop while connecting → the backend's imminent "listening" event
      // overwrites idle and the session appears to restart automatically.
      if (!isActiveRef.current) return
      const d = data as { status: VoiceStatus }
      dispatch(setVoiceStatus(d.status))

    }

    const onTranscript = (data: unknown) => {
      if (!isActiveRef.current) return
      const d = data as { text: string; role: 'user' | 'assistant'; final: boolean }
      if (d.final || d.text) {
        dispatch(setTranscript({ text: d.text, role: d.role, final: d.final }))
      }
    }

    const onAudio = (data: unknown) => {
      if (!isActiveRef.current || isSpeakerMutedRef.current) return
      const d = data as { data: string }
      audioPlayerRef.current.enqueue(d.data)
    }

    // Agent was interrupted mid-speech (user spoke while agent was talking).
    // Discard every buffered/playing audio chunk immediately so the agent's
    // stale audio doesn't keep playing over the user's new turn.
    const onAudioInterrupted = () => {
      if (!isActiveRef.current) return
      audioPlayerRef.current.clear()
      audioPlayerRef.current = makePlayer()
      dispatch(clearStreamingTranscript())
    }

    const onAction = (data: unknown) => {
      if (!isActiveRef.current) return
      void dispatchAction(data as Record<string, unknown>)
    }

    const onGetState = () => {
      if (!isActiveRef.current) return
      sendStateSync()
    }

    // One-off config push from OnboardingSessionManager.start() — currently just the QA-only
    // text-to-SAM chip toggle. Not gated by isActiveRef: it can arrive in the same tick the
    // session is created, before isActiveRef flips true.
    const onConfig = (data: unknown) => {
      const d = data as { debug_text_input_enabled?: boolean }
      if (typeof d?.debug_text_input_enabled === 'boolean') {
        dispatch(setDebugTextInputEnabled(d.debug_text_input_enabled))
      }
    }

    // The server's realtime connection is up; it tells us how often to send keepalives.
    const onSessionReady = (data: unknown) => {
      const d = data as { session_id?: string }
      if (!isActiveRef.current || !d?.session_id || d.session_id !== sessionIdRef.current) return
      setKeepalive(keepaliveFromSessionReady(d.session_id, data))
    }

    const onError = (data: unknown) => {
      const d = data as { message?: string; code?: string; fatal?: boolean }
      console.error('[Voice] error:', d.message, d.code)
      // Session lost (e.g. backend session gone) — stop streaming to a dead session, return the
      // mic to idle, and leave a notice so the user can tap to start a fresh session. This also
      // breaks the audio→session_not_found loop.
      if (d.fatal || d.code === 'session_not_found') {
        isActiveRef.current = false
        setKeepalive(null)
        if (editWatcherRef.current) { clearInterval(editWatcherRef.current); editWatcherRef.current = null }
        audioCapture.stop()
        audioPlayerRef.current.clear()
        audioPlayerRef.current = makePlayer()
        dispatch(setSessionNotice('Voice session ended. Tap the mic to resume.'))
        dispatch(resetVoice())
      }
      // Non-fatal errors are recovered server-side; the session stays live, so keep the voice status.
    }

    socketService.on('onboarding:status', onStatus)
    socketService.on('onboarding:transcript', onTranscript)
    socketService.on('onboarding:audio', onAudio)
    socketService.on('onboarding:audio_interrupted', onAudioInterrupted)
    socketService.on('onboarding:action', onAction)
    socketService.on('onboarding:get_state', onGetState)
    socketService.on('onboarding:error', onError)
    socketService.on('onboarding:config', onConfig)
    socketService.on('onboarding:session_ready', onSessionReady)

    return () => {
      socketService.off('onboarding:status', onStatus)
      socketService.off('onboarding:transcript', onTranscript)
      socketService.off('onboarding:audio', onAudio)
      socketService.off('onboarding:audio_interrupted', onAudioInterrupted)
      socketService.off('onboarding:action', onAction)
      socketService.off('onboarding:get_state', onGetState)
      socketService.off('onboarding:error', onError)
      socketService.off('onboarding:config', onConfig)
      socketService.off('onboarding:session_ready', onSessionReady)
    }
  }, [dispatch, dispatchAction, sendStateSync, audioCapture, makePlayer])

  // -------------------------------------------------------------------------
  // Session lifecycle
  // -------------------------------------------------------------------------
  const startVoice = useCallback(async () => {
    if (isActiveRef.current) return // prevent double-start
    isActiveRef.current = true

    const sessionId = crypto.randomUUID()
    sessionIdRef.current = sessionId
    lastSubmitResultRef.current = null
    dispatch(setSessionId(sessionId))
    dispatch(setSessionNotice(null)) // clear any "session lost" banner from a prior session
    dispatch(setVoiceStatus('connecting'))

    const meta = getMetaRef.current()
    const handle = getActiveHandleRef.current()
    const initStepState = handle?.getState() ?? userStepCbRef.current?.getState?.() ?? null

    console.info('[Voice] startVoice | sessionId=%s step=%s socketConnected=%s',
      sessionId, meta.currentStep, socketService.isConnected)
    socketService.emit('message', {
      type: 'onboarding.session.start',
      session_id: sessionId,
      keepalive: true, // we send onboarding.keepalive (useVoiceKeepalive) — opts into the silence sweep
      currentStep: meta.currentStep,
      completedSteps: meta.completedSteps,
      // Company name forwarded from the tenant API so the backend can inject it into the
      // confirmation-page trigger without a separate DB round-trip that might be stale.
      company_name: meta.company_name || '',
      stepState: initStepState,
      lastSubmitResult: null,
      // False only on the very first start this page load — backend uses this to play the
      // full SAM intro greeting exactly once; every stop→restart gets a quiet continuation.
      intro_done: hasHeardIntroRef.current,
    })
    hasHeardIntroRef.current = true

    // ── Manual-interaction watcher: tell the agent when the user acts by hand ─────────────
    // Diff {step, stepState} on an interval and notify the agent of manual actions:
    //   • step changed → user navigated by hand → emit immediately (kind=navigation → continue)
    //   • field changed → user edited a field → emit ONCE IT SETTLES (unchanged for one tick), so
    //     continuous typing doesn't fire mid-word; transcript names the changed field(s) so the
    //     agent can acknowledge ("Got it, added the company name") and offer the next field.
    // Changes within 2.5s of an agent action are the agent's own and are absorbed, not reported.
    const initSnap = JSON.stringify({ step: meta.currentStep, state: watchableState(handle?.getState() ?? null) })
    lastSnapshotRef.current = initSnap
    lastSeenRef.current = initSnap
    if (editWatcherRef.current) clearInterval(editWatcherRef.current)
    editWatcherRef.current = setInterval(() => {
      if (!isActiveRef.current) return
      const m = getMetaRef.current()
      const current = watchableState(getActiveHandleRef.current()?.getState() ?? null)
      const snap = JSON.stringify({ step: m.currentStep, state: current })
      if (snap === lastSnapshotRef.current) { lastSeenRef.current = snap; return } // nothing new

      // Agent's own change — absorb the new baseline, don't report it as the user's.
      if (Date.now() - lastAgentActionAtRef.current < 2500) {
        lastSnapshotRef.current = snap
        lastSeenRef.current = snap
        return
      }

      let prevStep = ''
      let prevState: Record<string, unknown> = {}
      try {
        const parsed = JSON.parse(lastSnapshotRef.current)
        prevStep = (parsed?.step as string) ?? ''
        prevState = (parsed?.state as Record<string, unknown>) ?? {}
      } catch { /* ignore */ }
      const stepChanged = prevStep !== '' && prevStep !== m.currentStep

      // Field edit: wait until the value has settled (unchanged since the previous tick) so we
      // don't message the agent in the middle of typing.
      if (!stepChanged && snap !== lastSeenRef.current) { lastSeenRef.current = snap; return }

      lastSnapshotRef.current = snap
      lastSeenRef.current = snap

      let transcript: string
      if (stepChanged) {
        transcript = `The user moved to the "${m.currentStep}" step on their own.`
      } else {
        // Name the field(s) that changed so the agent can acknowledge specifically.
        const changed = describeStepChange(current as Record<string, unknown> | null, prevState)
        transcript = changed.length
          ? `The user just filled these by hand: ${changed.join('; ')}.`
          : 'The user manually updated the form.'
      }

      socketService.emit('message', {
        type: 'onboarding.user_input',
        session_id: sessionIdRef.current,
        currentStep: m.currentStep,
        completedSteps: m.completedSteps,
        stepState: current,
        lastSubmitResult: lastSubmitResultRef.current,
        kind: stepChanged ? 'navigation' : 'edit',
        lastUserUtterance: { transcript },
      })
    }, 1500)

    await audioCapture.start()
  }, [dispatch, audioCapture])

  const stopVoice = useCallback(() => {
    // Always clean up even if isActiveRef drifted out of sync (e.g. error state left session
    // active in redux but the ref was cleared — this prevents the stop button from doing nothing).
    const wasActive = isActiveRef.current
    isActiveRef.current = false
    setKeepalive(null)

    console.info('[Voice] stopping session (wasActive=%s)', wasActive)
    if (editWatcherRef.current) { clearInterval(editWatcherRef.current); editWatcherRef.current = null }
    if (sessionIdRef.current) {
      socketService.emit('message', { type: 'onboarding.session.end', session_id: sessionIdRef.current })
    }
    audioCapture.stop()
    audioPlayerRef.current.clear()
    audioPlayerRef.current = makePlayer()
    dispatch(resetVoice())
  }, [dispatch, audioCapture])

  // Stop voice on unmount if still active.
  useEffect(() => {
    return () => {
      if (editWatcherRef.current) { clearInterval(editWatcherRef.current); editWatcherRef.current = null }
      if (isActiveRef.current) {
        console.info('[Voice] unmounting with active session — stopping')
        socketService.emit('message', { type: 'onboarding.session.end', session_id: sessionIdRef.current })
        audioPlayerRef.current.clear()
        isActiveRef.current = false
      }
    }
  }, []) // empty deps — runs only on unmount

  // Immediately notify the agent of a UI-driven field change (bypasses the 1.5s diff watcher).
  // Used when the user interacts with a picker/toggle in the UI while voice is active and we want
  // the agent to respond right away — e.g. tapping a voice card on the voice step.
  const emitFieldChange = useCallback((diff: string) => {
    if (!isActiveRef.current) return
    const meta = getMetaRef.current()
    const stepState = getActiveHandleRef.current()?.getState() ?? userStepCbRef.current?.getState?.() ?? null
    lastAgentActionAtRef.current = 0 // reset agent-action dampener so the change isn't absorbed
    socketService.emit('message', {
      type: 'onboarding.user_input',
      session_id: sessionIdRef.current,
      kind: 'field_edit',
      currentStep: meta.currentStep,
      completedSteps: meta.completedSteps,
      stepState,
      diff,
      lastSubmitResult: lastSubmitResultRef.current,
    })
  }, [])

  // ─────── TEST ONLY — REVERT BEFORE PRODUCTION ───────
  // Sends a typed message straight to the live onboarding voice session, simulating what the user
  // would say by mic. Gated by chat_settings.interactive_text_input_enabled in the wizard page.
  const sendText = useCallback((text: string) => {
    if (!isActiveRef.current || !sessionIdRef.current) {
      console.warn('[Voice:TEST] sendText called but no active session — dropped')
      return
    }
    console.info('[Voice:TEST] sendText → onboarding.text | text=%s', text)
    socketService.emit('message', {
      type: 'onboarding.text',
      session_id: sessionIdRef.current,
      text,
    })
    // The server sends typed text straight to the model and never echoes it back as a transcript,
    // so record it here — the debug transcript shows both sides of the conversation.
    dispatch(setTranscript({ text, role: 'user', final: true }))
  }, [dispatch])
  // ─────────────────────────────────────────────────────

  return {
    startVoice,
    stopVoice,
    emitFieldChange,
    sendStateSync,
    isMuted,
    toggleMute,
    isSpeakerMuted,
    toggleSpeakerMute,
    showTranscript,
    toggleTranscript,
    isCapturing: audioCapture.isCapturing,
    captureError: audioCapture.error,
    // ─────── TEST ONLY ───────
    sendText,
    // ─────────────────────────
  }
}
